import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useLocale } from '../../../../hooks/useLocale';
import { logToNewRelic } from '../../../../services/debug';
import { useFlag } from '../../../../services/launchDarkly';
import { WIDGET_SAFE_ID_PREFIX } from '../../../../stores/event-trigger/constants';
import { RoadLocalizedAssetKey, type EventTriggerPopupData } from '../../../../stores/event-trigger/types';
import { useStores } from '../../../../stores/useStores';
import { getErrorMessage } from '../../../../utils/errors';
import {
  isMessagePopupType,
  isOfferPopupType,
  isOfferV2PopupType,
  isQuestEventTrigger,
  isRoadEventTrigger,
  isRollingOfferPopupType,
  isWithinRewardClaimWindow,
} from '../../../../utils/events';
import { getImageWidgetUrl, getRoadLocalizedUrl } from '../../../../utils/localizationUtils';

export const EventTriggerWidget = observer(
  ({ event, dataTestId = 'event-trigger-widget' }: { event: EventTriggerPopupData; dataTestId?: string }) => {
    const { eventTriggerStore, questStore } = useStores();

    const { currentPlayerQuest, questsFlag } = questStore;
    const { locale } = useLocale();
    const offerLocalizationFF = useFlag('offer_localization', false);
    const messageLocalizationFF = useFlag('message_localization', false);

    const { t } = useTranslation();
    const timer = eventTriggerStore.getEventTimerFormatted(event.userEventTriggerId, t);

    const handleWidgetClick = async () => {
      try {
        eventTriggerStore.sendAnalyticsEvents(
          {
            event_action: 'impression',
            triggered_by: 'click',
            click_location: 'widget',
          },
          event,
        );
        await eventTriggerStore.openPopupByUserEventTriggerId(event.userEventTriggerId);
      } catch (e) {
        const errorMessage = getErrorMessage(e);
        logToNewRelic('Widget click error', errorMessage, 'error');
      }
    };

    if (
      !isOfferPopupType(event.benefit) &&
      !isMessagePopupType(event.benefit) &&
      !isOfferV2PopupType(event.benefit) &&
      !isQuestEventTrigger(event.benefit) &&
      !isRollingOfferPopupType(event.benefit) &&
      !isRoadEventTrigger(event.benefit)
    ) {
      return null;
    }

    if (isQuestEventTrigger(event.benefit) && !questsFlag) {
      return null;
    }

    if (isMessagePopupType(event.benefit) && !event.benefit.miniImageUrl) {
      return null;
    }

    const widgetImageUrl = (() => {
      if (isQuestEventTrigger(event.benefit)) {
        return event.benefit.widgetImageUrl;
      }

      if (isRoadEventTrigger(event.benefit)) {
        return getRoadLocalizedUrl(event.benefit.assets, RoadLocalizedAssetKey.WIDGET, locale);
      }

      return getImageWidgetUrl({
        locale,
        benefit: event.benefit,
        offerLocalizationFF,
        messageLocalizationFF,
      });
    })();

    const hideWidgetTimer =
      !event.canDisplay ||
      event.eventTrigger.hideWidgetTimer ||
      (isQuestEventTrigger(event.benefit) && !!currentPlayerQuest?.hideTimer) ||
      isWithinRewardClaimWindow(event.expiredAt, event.rewardClaimEndDate);

    const { userEventTriggerId } = event;

    return (
      <div className="home-widget" data-testid={dataTestId}>
        <div className="circle-parent" data-testid="event-trigger-home-widget-button" onClick={handleWidgetClick}>
          <div className="circle" id={`${WIDGET_SAFE_ID_PREFIX}${userEventTriggerId}`}></div>
          <img alt="Event Mini Image" src={widgetImageUrl} />
          {timer && !hideWidgetTimer && <div className="timer">{timer}</div>}
        </div>
      </div>
    );
  },
);
